import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { useFormik } from 'formik';

import useFormSubmit from 'hooks/useFormSubmit';
import example from 'templates/crm-lead-upload-template.xlsx';
import FormTreeSelectField from 'components/ui/form/FormTreeSelectField';
import FormFileUploadField from 'components/ui/form/FormFileUploadField';

const validationSchema = yup.object({
  block_id: yup.string().nullable(),
  block_item_id: yup
    .string()
    .required('crm.block.column.addModal.validation.crm_block_item_id'),
});

const CRMFileUploadModal = (props) => {
  const { open, setOpen } = props;
  const { t } = useTranslation();
  const { submit, isSubmitting } = useFormSubmit();

  const formik = useFormik({
    initialValues: {
      block_id: '',
      block_item_id: '',
      file: null,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      submit(
        { type: 'post', contentType: 'formData' },
        values,
        '/import/leads',
        values?.name,
        null,
        true,
        refetch
      );
    },
  });

  const refetch = () => {
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = example;
    a.download = 'template.xlsx';
    a.click();
    a.remove();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='lg'
      disableEscapeKeyDown={true}
      PaperProps={{
        className: '!base-border !shadow-md',
      }}
    >
      <DialogTitle id='alert-dialog-title' className='!pt-1.5'>
        <span className='pr-5'>Import</span>
        <div className='close-btn-wrapper'>
          <IconButton variant='onlyIcon' color='primary' onClick={handleClose}>
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <div>
            <div className='max-w-md text-sm'>
              {t('crm.block.fileUploadModal.title')}
            </div>
            <div className='flex gap-2 mt-2'>
              <div className='[&_.MuiFormControl-root]:!m-0'>
                <FormFileUploadField
                  delay={0.0013}
                  accept='.xls, .xlsx, .csv'
                  fieldName='file'
                  formik={formik}
                  label={
                    <>
                      <i className='bi bi-file-earmark-arrow-up' />
                      {t('common.fields.uploadFile')}
                    </>
                  }
                  btnLabel={t('common.fields.uploadFile')}
                  fileSize={1}
                />
              </div>
              <Button
                variant='outlined'
                size='small'
                color='inherit'
                startIcon={<i className='bi bi-file-earmark-arrow-down' />}
                onClick={handleDownload}
              >
                {t('common.fields.downloadExample')}
              </Button>
            </div>
            <div className='[&_.MuiLinearProgress-root]:hidden [&_.MuiFormControl-root]:!mt-1 [&_.MuiFormControl-root]:!mb-0 border rounded px-1 my-2'>
              <FormTreeSelectField
                delay={0}
                duration={0}
                parentFieldName='block_id'
                childFieldName='block_item_id'
                label=''
                childOptionField={'pipes'}
                formik={formik}
                parentLabel={'name'}
                parentValue={'id'}
                childLabel={'name'}
                childValue={'id'}
                variant='standard'
                path='/crm/block'
                size='small'
                isitemsizesmall={true}
              />
            </div>

            <div className='mt-3 text-right'>
              <Button
                variant='contained'
                type='submit'
                sx={{ borderRadius: '25px' }}
                disabled={!formik.values.file || isSubmitting}
                endIcon={
                  isSubmitting ? (
                    <CircularProgress size={20} color='info' />
                  ) : undefined
                }
              >
                {t('common.button.save')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default memo(CRMFileUploadModal);
